import { useMemo, useState, MouseEvent } from "react";
import clsx from "clsx";

import { TransactionsFilterModel, DropdownItemModel } from "@interfaces/index";
import {
  ChevronDownSvg,
  TrashXSvg,
  Button,
  ChecksSvg,
  TrashSvg,
  PaperClipSvg,
  PaperClipOffSvg,
  LockOpenSvg,
  LockSvg,
  ReceiptRefundSvg,
  ReceiptOffSvg,
} from "@components/index";

export const TransactionsFilter = ({
  value,
  onSetFilters,
  dataTestid,
}: {
  value?: TransactionsFilterModel;
  onSetFilters: (value: TransactionsFilterModel) => void;
  dataTestid?: string;
}) => {
  const [isShown, setIsShown] = useState(false);

  const [status, setStatus] = useState("");
  const [attachment, setAttachment] = useState("");
  const [refund, setRefund] = useState("");

  const statuses: DropdownItemModel[] = [
    {
      id: "",
      title: "All",
    },
    {
      id: "open",
      title: (
        <>
          <span className="mr-1 *:size-5">
            <LockOpenSvg />
          </span>
          Open
        </>
      ),
    },
    {
      id: "closed",
      title: (
        <>
          <span className="mr-1 *:size-5">
            <LockSvg />
          </span>
          Closed
        </>
      ),
    },
  ];

  const attachments: DropdownItemModel[] = [
    {
      id: "",
      title: "All",
    },
    {
      id: "1",
      title: (
        <>
          <span className="mr-1 *:size-5">
            <PaperClipSvg />
          </span>
          Show
        </>
      ),
    },
    {
      id: "0",
      title: (
        <>
          <span className="mr-1 *:size-5">
            <PaperClipOffSvg />
          </span>
          Hide
        </>
      ),
    },
  ];

  const refunds: DropdownItemModel[] = [
    {
      id: "",
      title: "All",
    },
    {
      id: "1",
      title: (
        <>
          <span className="mr-1 *:size-5">
            <ReceiptRefundSvg />
          </span>
          Show
        </>
      ),
    },
    {
      id: "0",
      title: (
        <>
          <span className="mr-1 *:size-5">
            <ReceiptOffSvg />
          </span>
          Hide
        </>
      ),
    },
  ];

  const buttonLabel = useMemo(() => {
    let counter = ["open", "closed"].includes(value?.status || "") ? 1 : 0;
    counter += ["0", "1"].includes(value?.attachment || "") ? 1 : 0;
    counter += ["0", "1"].includes(value?.refund || "") ? 1 : 0;
    return counter === 0 ? (
      "Filters"
    ) : (
      <>
        Applied:
        <span className="ml-1 rounded-full bg-primary-108 px-2 py-1 text-100 font-bold text-primary-300">
          {counter}
        </span>
      </>
    );
  }, [value]);

  const handleOpen = () => {
    const status = statuses.find(({ id }) => id === value?.status)?.id || "";
    setStatus(status);
    const attachment =
      attachments.find(({ id }) => id === value?.attachment)?.id || "";
    setAttachment(attachment);
    const refund = refunds.find(({ id }) => id === value?.refund)?.id || "";
    setRefund(refund);
    setIsShown(true);
  };

  const handleChoose = () => {
    setIsShown(false);
    onSetFilters({ status, attachment, refund });
  };

  const handleClear = () => {
    setIsShown(false);
    onSetFilters({ status: "", attachment: "", refund: "" });
  };

  const handleClearDropdownValue = (event: MouseEvent) => {
    event.stopPropagation();
    onSetFilters({ status: "", attachment: "", refund: "" });
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="flex h-12 w-full items-center justify-between rounded-lg border border-black-16 bg-white-100 px-4 py-3 text-black-100 hover:border-black-32 md:w-36"
        data-testid={dataTestid}
      >
        <div>{buttonLabel}</div>
        <div className="shrink-0">
          {(value?.status || value?.attachment || value?.refund) && !isShown ? (
            <div onClick={handleClearDropdownValue}>
              <TrashXSvg />
            </div>
          ) : (
            <div className="text-black-54">
              <ChevronDownSvg />
            </div>
          )}
        </div>
      </button>

      {isShown && (
        <div
          className="fixed inset-0 z-10 flex items-center justify-center bg-black-54 p-6"
          onClick={() => setIsShown(false)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-white-100 p-6 md:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-300 text-black-100">By Status</div>
            <div className="flex gap-x-2 pt-4">
              {statuses.map(({ id, title }) => (
                <button
                  key={id}
                  type="button"
                  className={clsx(
                    "flex items-center justify-center rounded-lg border border-black-8 px-4 py-2.5 text-black-54 hover:border-primary-300",
                    id === status && "border-primary-300 text-primary-300",
                  )}
                  onClick={() => setStatus(id)}
                >
                  {title}
                </button>
              ))}
            </div>
            <div className="pt-6 text-300 text-black-100">By Attachments</div>
            <div className="flex gap-x-2 pt-4">
              {attachments.map(({ id, title }) => (
                <button
                  key={id}
                  type="button"
                  className={clsx(
                    "flex items-center justify-center rounded-lg border border-black-8 px-4 py-2.5 text-black-54 hover:border-primary-300",
                    id === attachment && "border-primary-300 text-primary-300",
                  )}
                  onClick={() => setAttachment(id)}
                >
                  {title}
                </button>
              ))}
            </div>
            <div className="pt-6 text-300 text-black-100">By Refund</div>
            <div className="flex gap-x-2 pt-4">
              {refunds.map(({ id, title }) => (
                <button
                  key={id}
                  type="button"
                  className={clsx(
                    "flex items-center justify-center rounded-lg border border-black-8 px-4 py-2.5 text-black-54 hover:border-primary-300",
                    id === refund && "border-primary-300 text-primary-300",
                  )}
                  onClick={() => setRefund(id)}
                >
                  {title}
                </button>
              ))}
            </div>
            <div className="flex justify-between pt-10">
              <Button
                title="Apply filters"
                leftIcon={<ChecksSvg />}
                height="small"
                onClick={handleChoose}
              />
              <Button
                title="Clear all"
                leftIcon={<TrashSvg />}
                variant="text"
                height="small"
                onClick={handleClear}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
